import React from 'react';
import { Zap } from 'lucide-react';
import ChartCard from './ChartCard';

const Stat = ({ label, value, testId }) => (
  <div>
    <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
    <p className="font-mono text-base font-bold text-autoxium-blue" data-testid={testId}>{value}</p>
  </div>
);

const SubGroup = ({ title, children }) => (
  <div className="space-y-3">
    <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/70 border-b border-structure pb-1">{title}</p>
    <div className="grid grid-cols-2 gap-3">{children}</div>
  </div>
);

/**
 * Motor Requirement (§4.2): duty-rated motor sizing outputs. Continuous Power (S1, IEC
 * 60034-1) is sized to the worst-case sustained scenario, never just cruise. Continuous
 * Power (30 min) is the S2 short-time overload rating. Peak is the short-duration
 * acceleration/hill-start rating. See backend/simulation/motor_sizing.py for the full
 * duty-type mapping and formulas.
 */
const MotorRequirementPanel = ({ motorRequirement: mr }) => {
  if (!mr) return null;

  return (
    <ChartCard icon={Zap} title="Motor Requirement" description="Duty-rated motor sizing (IEC 60034-1)." testId="motor-requirement-panel">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <SubGroup title="Continuous Power (S1)">
          <Stat label="Without Margin" value={`${mr.continuous_power_kw.toFixed(1)} kW`} testId="mr-continuous-power" />
          <Stat label="With Margin" value={`${mr.continuous_power_with_margin_kw.toFixed(1)} kW`} testId="mr-continuous-power-margin" />
        </SubGroup>
        <SubGroup title="Continuous Power (30 min, S2)">
          <Stat label="Without Margin" value={`${mr.continuous_power_30min_kw.toFixed(1)} kW`} testId="mr-continuous-30min" />
          <Stat label="With Margin" value={`${mr.continuous_power_30min_with_margin_kw.toFixed(1)} kW`} testId="mr-continuous-30min-margin" />
        </SubGroup>
        <SubGroup title="Peak Power">
          <Stat label="Without Margin" value={`${mr.peak_power_kw.toFixed(1)} kW`} testId="mr-peak-power" />
          <Stat label="With Margin" value={`${mr.peak_power_with_margin_kw.toFixed(1)} kW`} testId="mr-peak-power-margin" />
        </SubGroup>
        <SubGroup title="Peak Torque">
          <Stat label="Without Margin" value={`${mr.peak_torque_nm.toFixed(0)} Nm`} testId="mr-peak-torque" />
          <Stat label="With Margin" value={`${mr.peak_torque_with_margin_nm.toFixed(0)} Nm`} testId="mr-peak-torque-margin" />
        </SubGroup>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-structure sm:w-1/2">
        <Stat label="Base Speed" value={`${mr.base_speed_rpm.toFixed(0)} RPM`} testId="mr-base-speed" />
        <Stat label="Maximum Speed" value={`${mr.max_speed_rpm.toFixed(0)} RPM`} testId="mr-max-speed" />
      </div>
    </ChartCard>
  );
};

export default MotorRequirementPanel;
